import React, { useState } from 'react';
import moment, { Moment } from 'moment';
import { Box, Button, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { getActionHandler } from 'src/modules/assistant/actions/registry';
import { ACTION_TYPE } from 'src/modules/assistant/constants/actions';
import {
    getActionVisual,
    getActionVisualStyle,
} from 'src/modules/assistant/helpers/actionVisuals';
import { CHANNEL_BLOCK_REASON } from 'src/modules/conversations/channels';
import { TActionDraftHandlers } from 'src/modules/assistant/types/actionDraft';
import {
    TActionDraft,
    TActionExecution,
} from 'src/modules/assistant/types/message';
import { AICallProgress } from './AICallProgress';
import { DraftFields } from './DraftFields';
import { DraftResult } from './DraftResult';
import { TEditableDraft } from './types';
import { useDraftStyles } from './styles';

const DRAFT_STATUS = Object.freeze({
    READY: 'ready',
    EXECUTING: 'executing',
    COMPLETED: 'completed',
    ERROR: 'error',
    CANCELLED: 'cancelled',
} as const);

type TDraftStatus = (typeof DRAFT_STATUS)[keyof typeof DRAFT_STATUS];

const USEFUL_FEEDBACK = 'useful';
const TITLE_ICON_SIZE = 17;
const BUTTON_ICON_SIZE = 15;
const CHANNEL_BLOCK_REASONS = new Set<string>(
    Object.values(CHANNEL_BLOCK_REASON)
);
const HIDDEN_MISSING_FIELDS = new Set([
    'document',
    'documents',
    'documentation',
    'attachments',
]);

export interface AssistantActionDraftProps extends TActionDraftHandlers {
    action: TActionDraft;
    execution?: TActionExecution;
    feedback?: string;
    channel?: string | null;
}

const toMoment = (date?: string) => (date ? moment(date) : null);

/**
 * Editable confirmation of an action proposed by the assistant: lets the user
 * adjust the draft, execute it and see the result.
 */
export const AssistantActionDraft = ({
    action,
    execution,
    feedback,
    channel,
    onContinue,
    onCancelExecution,
    onExecute,
    onLike,
}: AssistantActionDraftProps) => {
    const classes = useDraftStyles();
    const theme = useTheme();
    const { t } = useTranslation();
    const visual = getActionVisual(action.type, channel);
    const visualStyle = getActionVisualStyle(visual, theme);
    const { Icon } = visual;
    const handler = getActionHandler(action.type);
    const [draft, setDraft] = useState<TEditableDraft>(
        action as TEditableDraft
    );
    const [status, setStatus] = useState<TDraftStatus>(
        execution?.completedAt ? DRAFT_STATUS.COMPLETED : DRAFT_STATUS.READY
    );
    const [recorded, setRecorded] = useState(true);
    const [errorCode, setErrorCode] = useState<string | null>(null);
    const [result, setResult] = useState<TActionExecution | null>(
        execution || null
    );
    const [completedAt, setCompletedAt] = useState<Moment | null>(
        toMoment(execution?.completedAt)
    );
    const [nextFollowUpAt, setNextFollowUpAt] = useState<Moment | null>(
        toMoment(execution?.nextFollowUpAt)
    );
    const [liked, setLiked] = useState(feedback === USEFUL_FEEDBACK);
    const isAICall = action.type === ACTION_TYPE.AI_CALL;
    const isExecuting = status === DRAFT_STATUS.EXECUTING;
    const isCompleted = status === DRAFT_STATUS.COMPLETED;
    const canExecute = Boolean(handler?.validate(draft));
    const cancellableWhileExecuting =
        isExecuting && Boolean(handler?.cancellable);
    const missingFields = (draft.missingFields || []).filter(
        (field) =>
            !HIDDEN_MISSING_FIELDS.has(String(field).trim().toLowerCase())
    );

    const execute = async () => {
        setStatus(DRAFT_STATUS.EXECUTING);
        const outcome = await onExecute(draft);
        if (!outcome?.ok) {
            setErrorCode(outcome?.error || null);
            setStatus(DRAFT_STATUS.ERROR);
            return;
        }
        const data = (outcome.data || {}) as TActionExecution;
        setResult(data);
        setRecorded(outcome.recorded !== false);
        setCompletedAt(moment(data.completedAt || undefined));
        setNextFollowUpAt(toMoment(data.nextFollowUpAt));
        setStatus(DRAFT_STATUS.COMPLETED);
    };

    const like = async () => {
        if (await onLike()) setLiked(true);
    };

    const renderFooter = () => {
        if (isCompleted)
            return (
                <DraftResult
                    completedAt={completedAt}
                    execution={result}
                    isAICall={isAICall}
                    liked={liked}
                    nextFollowUpAt={nextFollowUpAt}
                    onContinue={onContinue}
                    onLike={like}
                    recorded={recorded}
                />
            );
        if (status === DRAFT_STATUS.ERROR)
            return (
                <Alert severity="error">
                    {CHANNEL_BLOCK_REASONS.has(errorCode)
                        ? t(`Assistant.ChannelBlocked.${errorCode}`)
                        : t('Assistant.ActionError')}
                </Alert>
            );
        if (isExecuting && isAICall) return <AICallProgress />;
        return (
            <Box display="flex" flexWrap="wrap" gridGap={7}>
                <Button
                    className={classes.actionButton}
                    disabled={isExecuting || !canExecute}
                    onClick={execute}
                    size="small"
                    startIcon={
                        <Icon
                            aria-hidden="true"
                            height={BUTTON_ICON_SIZE}
                            width={BUTTON_ICON_SIZE}
                        />
                    }
                    style={visualStyle}
                    variant="contained"
                >
                    {isExecuting
                        ? t('Assistant.ActionStatus.executing')
                        : t('Assistant.ConfirmAction')}
                </Button>
                <Button
                    disabled={isExecuting && !cancellableWhileExecuting}
                    onClick={() =>
                        cancellableWhileExecuting
                            ? onCancelExecution()
                            : setStatus(DRAFT_STATUS.CANCELLED)
                    }
                    size="small"
                    variant="outlined"
                >
                    {cancellableWhileExecuting
                        ? t('Assistant.EndCall')
                        : t('Assistant.CancelAction')}
                </Button>
            </Box>
        );
    };

    if (status === DRAFT_STATUS.CANCELLED) return null;

    return (
        <div className={classes.root}>
            <div className={classes.titleRow} style={visualStyle}>
                <span className={classes.actionIcon}>
                    <Icon
                        aria-hidden="true"
                        height={TITLE_ICON_SIZE}
                        width={TITLE_ICON_SIZE}
                    />
                </span>
                <span>
                    <Typography variant="subtitle2">
                        {t(`Assistant.ActionType.${action.type}`)}
                    </Typography>
                    {action.summary && (
                        <Typography variant="body2">
                            {action.summary}
                        </Typography>
                    )}
                </span>
            </div>
            {!isCompleted && action.type === ACTION_TYPE.SEND_TEMPLATE && (
                <Typography variant="body2">{draft.templateTitle}</Typography>
            )}
            {!isCompleted && (
                <DraftFields
                    draft={draft}
                    handler={handler}
                    onChange={(changes) =>
                        setDraft((current) => ({ ...current, ...changes }))
                    }
                />
            )}
            {!!missingFields.length &&
                status === DRAFT_STATUS.READY &&
                !canExecute && (
                    <Typography color="error" variant="caption">
                        {t('Assistant.ActionMissing')}:{' '}
                        {missingFields.join(', ')}
                    </Typography>
                )}
            {status === DRAFT_STATUS.READY && isAICall && (
                <Alert severity="info">
                    <strong>{t('Assistant.AICallConfirmation')}</strong>
                    <br />
                    {t('Assistant.AICallConfirmationHint')}
                </Alert>
            )}
            {renderFooter()}
        </div>
    );
};
